import {
  AERA_BUSINESS_OPTIONS,
  getBusinessFlowPreview,
  resolveBusinessTypeFromConfig,
  type AeraBusinessConfig,
  type AeraBusinessOption,
} from "@/app/components/AeraBusinessOptionsGrid";
import { workspacesApi, type Workspace } from "@/lib/api";
import {
  PROFILE_MAGIC_TAG_KEYS,
  readCanonicalTagValue,
} from "@/lib/profileMagicTagKeys";

export type WorkspaceSalesConfigDetail = {
  title: string;
  rows: [string, string][];
  buttons: [string, string];
};

type TagRow = { key?: string; value?: string };

const VALID_CONFIGS = new Set<AeraBusinessConfig>(["A", "B", "C", "D", "E"]);

const BILL_LIN_HELP_BY_CONFIG: Record<AeraBusinessConfig, string> = {
  A: "อ่านสลิป จับคู่ที่อยู่ และบันทึกยอดขาย",
  B: "วางบิล ติดตามงาน และบันทึกรายรับรายจ่าย",
  C: "ติดตามสมาชิก รอบชำระ และบันทึกรายการ",
  D: "บันทึกยอดขายหน้าร้านและตรวจสอบยอดเงิน",
  E: "เช็กยอดค้างและความเสี่ยงก่อนปิดออเดอร์",
};

const RISK_BEHAVIOR_BY_CONFIG: Record<AeraBusinessConfig, string> = {
  A: "ถามยืนยันเมื่อสลิปหรือที่อยู่ไม่ตรงกัน",
  B: "พักรายการไว้เมื่องานหรือยอดเงินยังไม่ชัด",
  C: "แจ้งเตือนเมื่อรอบชำระหรือข้อมูลสมาชิกไม่ครบ",
  D: "ถามยืนยันเมื่อยอดเงินสดไม่ตรงกับบิล",
  E: "พักรายการไว้ให้เจ้าของร้านตรวจ",
};

function normalizeTags(
  tags?: TagRow[] | null,
): Array<{ key: string; value: string }> {
  return (tags ?? [])
    .map((tag) => ({
      key: String(tag.key ?? "").trim(),
      value: String(tag.value ?? "").trim(),
    }))
    .filter((tag) => tag.key && tag.value);
}

function parseBusinessConfig(
  configRaw: string,
): AeraBusinessConfig | null {
  const normalized = configRaw.trim().toUpperCase();
  return VALID_CONFIGS.has(normalized as AeraBusinessConfig)
    ? (normalized as AeraBusinessConfig)
    : null;
}

export function resolveWorkspaceSalesConfigLabel(
  workspace?: Workspace | null,
): string {
  const tags = normalizeTags(workspace?.tags);
  const display = readCanonicalTagValue(tags, "business_type_display");
  if (display) return display;

  const config = parseBusinessConfig(
    readCanonicalTagValue(tags, PROFILE_MAGIC_TAG_KEYS.businessTypeConfig),
  );
  const option = config
    ? AERA_BUSINESS_OPTIONS.find((item) => item.config === config)
    : null;
  if (option) return option.label;

  return "ยังไม่ได้ตั้งค่า";
}

export function resolveWorkspaceSalesConfigSubtitle(
  workspace?: Workspace | null,
): string {
  const tags = normalizeTags(workspace?.tags);
  const businessType = resolveBusinessTypeFromConfig(
    readCanonicalTagValue(tags, PROFILE_MAGIC_TAG_KEYS.businessTypeConfig),
  );
  if (!businessType) return "ยังไม่ได้เลือกวิธีขาย";

  const preview = getBusinessFlowPreview(businessType);
  const parts = preview.subtitle
    .split("/")
    .map((part) => part.trim())
    .filter(Boolean);
  return parts.slice(0, 2).join(" · ") || preview.title;
}

function resolveBillLinHelpText(
  config: AeraBusinessConfig | null,
  businessType: ReturnType<typeof resolveBusinessTypeFromConfig>,
): string {
  if (businessType) {
    const fromPreview = getBusinessFlowPreview(businessType)
      .helps.map((help) => help.title)
      .filter(Boolean)
      .join(" · ");
    if (fromPreview) return fromPreview;
  }

  if (config) return BILL_LIN_HELP_BY_CONFIG[config];
  return "ช่วยบันทึกและตรวจสอบรายการเงินของร้าน";
}

function isDuplicateKeyError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const maybe = err as { message?: unknown; data?: unknown };
  if (
    typeof maybe.message === "string" &&
    maybe.message.includes("duplicate_key_value")
  ) {
    return true;
  }
  const data = maybe.data;
  if (!data || typeof data !== "object") return false;
  const d = data as { error?: unknown; details?: unknown };
  if (typeof d.error === "string" && d.error.includes("duplicate_key_value")) {
    return true;
  }
  const details = d.details;
  if (details && typeof details === "object") {
    const det = details as { error?: unknown };
    if (
      typeof det.error === "string" &&
      det.error.includes("duplicate_key_value")
    ) {
      return true;
    }
  }
  return false;
}

export async function upsertWorkspaceTagByKey(
  workspaceId: string,
  key: string,
  value: string,
  originalTags?: TagRow[] | null,
): Promise<void> {
  const tagKey = key.trim();
  const tagValue = value.trim();
  if (!tagKey || !tagValue) return;

  const rows = (originalTags ?? []).filter(
    (tag) => String(tag.key ?? "").trim() === tagKey,
  );
  if (
    rows.length === 1 &&
    String(rows[0]?.value ?? "").trim() === tagValue
  ) {
    return;
  }

  for (const row of rows) {
    const rowValue = String(row.value ?? "").trim();
    try {
      if (rowValue) {
        await workspacesApi.tags.deleteByKey(workspaceId, tagKey, rowValue);
      } else {
        await workspacesApi.tags.deleteByKey(workspaceId, tagKey);
      }
    } catch {
      // ignore
    }
  }

  try {
    await workspacesApi.tags.deleteByKey(workspaceId, tagKey);
  } catch {
    // ignore
  }

  try {
    await workspacesApi.tags.create(workspaceId, { key: tagKey, value: tagValue });
  } catch (err) {
    if (isDuplicateKeyError(err)) return;
    throw err;
  }
}

export async function saveWorkspaceBusinessTypeConfig(
  workspaceId: string,
  option: AeraBusinessOption,
  originalTags?: TagRow[] | null,
): Promise<void> {
  await upsertWorkspaceTagByKey(
    workspaceId,
    PROFILE_MAGIC_TAG_KEYS.businessTypeConfig,
    option.config,
    originalTags,
  );
  await upsertWorkspaceTagByKey(
    workspaceId,
    "business_type_display",
    option.label,
    originalTags,
  );
}

export function resolveWorkspaceBusinessType(
  workspace?: Workspace | null,
): ReturnType<typeof resolveBusinessTypeFromConfig> {
  const tags = normalizeTags(workspace?.tags);
  return resolveBusinessTypeFromConfig(
    readCanonicalTagValue(tags, PROFILE_MAGIC_TAG_KEYS.businessTypeConfig),
  );
}

export function buildWorkspaceSalesConfigDetail(
  workspace?: Workspace | null,
): WorkspaceSalesConfigDetail {
  const tags = normalizeTags(workspace?.tags);
  const configRaw = readCanonicalTagValue(
    tags,
    PROFILE_MAGIC_TAG_KEYS.businessTypeConfig,
  );
  const config = parseBusinessConfig(configRaw);
  const businessType = resolveBusinessTypeFromConfig(configRaw);
  const label = resolveWorkspaceSalesConfigLabel(workspace);

  return {
    title: "ตั้งค่าวิธีขายของร้าน",
    rows: [
      ["รูปแบบตอนนี้", label],
      [
        "Bill-Lin จะช่วย",
        resolveBillLinHelpText(config, businessType),
      ],
      [
        "เมื่อเจอความเสี่ยง",
        config
          ? RISK_BEHAVIOR_BY_CONFIG[config]
          : "ถามยืนยันก่อนบันทึกรายการ",
      ],
    ],
    buttons: ["แก้ไขวิธีขาย", "ยืนยันการตั้งค่า"],
  };
}
